import React, { useCallback, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import {
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@mui/material';
import { ArrowRight, FilterListOff } from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Iconify from 'src/components/iconify';
import Label from '../../label';
import CustomPopover, { usePopover } from '../../custom-popover';
import Scrollbar from '../../scrollbar';

type Props = {
  selected: { metrics_name: string; metrics_values: string[] }[];
  onFilters: (name: string, value: any[]) => void;
  metrics: { metrics_name: string; metrics_values: string[] }[];
  size?: number;
  useLabel?: boolean;
  margin?: number;
};

export default function FilterMetrics({
  selected,
  onFilters,
  metrics,
  size = 0,
  useLabel = true,
  margin = 2,
}: Props) {
  const { t } = useTranslation();
  const popover = usePopover();
  const theme = useTheme();
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const maxHeight = 390;

  const selectedMetrics = useMemo(
    () =>
      selected?.reduce(
        (acc, curr) => {
          acc[curr.metrics_name] = curr.metrics_values;
          return acc;
        },
        {} as Record<string, string[]>
      ),
    [selected]
  );

  const renderLabel = useMemo(
    () =>
      selected?.length
        ? selected
            .map(
              (m) =>
                `${m.metrics_name}: ${m.metrics_values?.slice(0, 2).join(',')} ${
                  m.metrics_values?.length > 2 ? '...' : ''
                }`
            )
            ?.slice(0, 2)
            ?.join(',')
        : t('text.labels.metrics'),
    [selected, t]
  );

  const handleFilterType = useCallback(
    (group: string, metric: string) => {
      const updatedMetrics = { ...selectedMetrics };
      if (!updatedMetrics[group]) {
        updatedMetrics[group] = [metric];
      } else {
        const index = updatedMetrics[group].indexOf(metric);
        if (index === -1) {
          updatedMetrics[group].push(metric);
        } else {
          updatedMetrics[group].splice(index, 1);
        }
      }
      onFilters(
        'metrics',
        Object.entries(updatedMetrics).map(([name, values]) => ({
          metrics_name: name,
          metrics_values: values,
        }))
      );
    },
    [selectedMetrics, onFilters]
  );

  const handleSelectAll = useCallback(
    (group: string, m: string[]) => {
      const updatedMetrics = { ...selectedMetrics };
      if (updatedMetrics[group]) {
        delete updatedMetrics[group];
      } else {
        updatedMetrics[group] = m.filter((value) =>
          value.toLowerCase().includes(searchText.toLowerCase())
        );
      }
      onFilters(
        'metrics',
        Object.entries(updatedMetrics).map(([name, values]) => ({
          metrics_name: name,
          metrics_values: values,
        }))
      );
    },
    [selectedMetrics, onFilters, searchText]
  );

  const filteredMetrics = useMemo(
    () =>
      metrics.filter(
        (m) =>
          m.metrics_name.toLowerCase().includes(searchText.toLowerCase()) ||
          m.metrics_values.some((value) => value.toLowerCase().includes(searchText.toLowerCase()))
      ),
    [metrics, searchText]
  );

  const metricLabelItems = (m: string) => {
    if (!selectedGroup) return null;
    return (
      <FormControlLabel
        key={m}
        control={<Checkbox checked={selectedMetrics[selectedGroup]?.includes(m) || false} />}
        label={m}
        onClick={() => handleFilterType(selectedGroup, m)}
        sx={{
          py: 1.5,
          textTransform: 'capitalize',
          '& .MuiFormControlLabel-label': {
            textTransform: 'capitalize',
          },
          // if the metric is like the serach text, highlight it and add a little animation
          ...(searchText &&
            m.toLowerCase().includes(searchText.toLowerCase()) && {
              fontWeight: 600,
              // backgroundColor: theme.palette.primary.lighter,
              color: theme.palette.primary.main,
              transition: theme.transitions.create(['background-color'], {
                duration: theme.transitions.duration.shortest,
              }),
            }),
        }}
      />
    );
  };

  const renderList = (metrics_values: string[] | undefined) => {
    if (!metrics_values) return <></>;
    if (!searchText)
      return metrics_values
        .sort((a, b) => a.localeCompare(b))
        .map((metric) => metricLabelItems(metric));

    // if there is a search text, sort the metrics by the search text
    const sortedMetrics = metrics_values
      .filter((value) => value.toLowerCase().includes(searchText.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
    const otherMetrics = metrics_values
      .filter((value) => !value.toLowerCase().includes(searchText.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
    return (
      <Stack divider={<Divider variant="middle" />}>
        {sortedMetrics.map((metric) => metricLabelItems(metric))}
        {otherMetrics.map((metric) => metricLabelItems(metric))}
      </Stack>
    );
  };

  return (
    <Stack m={margin} sx={{ width: size > 0 ? size : 'auto' }}>
      {useLabel && (
        <InputLabel
          shrink
          sx={{
            ml: 1,
            pr: 2,
            color: `${theme.palette.text.primary} !important`,
            fontSize: '1.1rem !important',
          }}
        >
          {t('text.labels.metrics')}
        </InputLabel>
      )}
      <Button
        variant="contained"
        onClick={popover.onOpen}
        disabled={metrics.length === 0}
        startIcon={
          <Iconify
            icon="eva:options-2-outline"
            sx={{
              mr: 0.5,
              color: theme.palette.action.active,
            }}
          />
        }
        endIcon={
          <Iconify
            icon={popover.open ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
            sx={{ ml: -0.5, color: theme.palette.action.active }}
          />
        }
        sx={{
          py: 0,
          px: 2,
          backgroundColor: 'background.paper',
          color: 'text.disabled',
          height: 53,
          justifyContent: 'space-between',
          border: `solid 1px ${theme.palette.divider}`,
          '&:hover': {
            border: `solid 1px ${theme.palette.action.active}`,
            bgcolor: 'background.paper',
          },
        }}
      >
        <Typography
          variant="inherit"
          fontSize={14}
          fontWeight={500}
          sx={{
            textTransform: 'capitalize',
            textAlign: 'left',
            width: '100%',
            color: 'text.disabled',
          }}
        >
          {t('text.labels.metrics')}
          {selected?.length > 0 && (
            <Label color="primary" sx={{ ml: 1 }}>
              {selected.length}
            </Label>
          )}
        </Typography>
      </Button>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{ p: 2.5, maxWidth: 650, maxHeight: 500, width: '100%' }}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <TextField
              label={`${t('text.labels.metrics')} ${t('text.labels.search').toLowerCase()}`}
              variant="filled"
              fullWidth
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Tooltip title={t('text.buttons.reset')}>
              <IconButton
                onClick={() => {
                  onFilters('metrics', []);
                  setSelectedGroup(null);
                }}
                disabled={Object.keys(selectedMetrics).length === 0}
              >
                <Badge
                  color="primary"
                  variant="dot"
                  invisible={Object.keys(selectedMetrics).length === 0}
                >
                  <FilterListOff />
                </Badge>
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack spacing={2} direction="row" alignItems="start">
            <Stack
              spacing={1.5}
              direction="column"
              width={1 / 2.5}
              sx={{ borderRight: 1, borderColor: 'divider' }}
            >
              <Scrollbar sx={{ maxHeight }}>
                <List>
                  {filteredMetrics.map((m) => (
                    <ListItem
                      key={m.metrics_name}
                      secondaryAction={
                        <ArrowRight
                          fontSize="small"
                          sx={{
                            color: theme.palette.text.disabled,
                          }}
                        />
                      }
                      disablePadding
                    >
                      <ListItemButton
                        onClick={() => setSelectedGroup(m.metrics_name)}
                        dense
                        sx={{
                          backgroundColor:
                            selectedGroup === m.metrics_name
                              ? theme.palette.action.selected
                              : 'transparent',
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={!!selectedMetrics[m.metrics_name]}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{ 'aria-labelledby': m.metrics_name }}
                            onClick={() => handleSelectAll(m.metrics_name, m.metrics_values)}
                          />
                        </ListItemIcon>
                        <ListItemText
                          id={m.metrics_name}
                          primary={m.metrics_name}
                          sx={{
                            textTransform: 'capitalize',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Scrollbar>
            </Stack>
            <Stack direction="column" justifyContent="flex-start" width={1.5 / 2.5}>
              {selectedGroup && (
                <Scrollbar sx={{ maxHeight }}>
                  <Stack px={2} divider={<Divider variant="middle" flexItem />}>
                    {renderList(
                      filteredMetrics
                        .find((metric) => metric.metrics_name === selectedGroup)
                        ?.metrics_values.filter((value) => value.trim() !== '')
                    )}
                  </Stack>
                </Scrollbar>
              )}
            </Stack>
          </Stack>
        </Stack>
      </CustomPopover>
    </Stack>
  );
}
